import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { StorefrontLayout } from '../../components/storefront/StorefrontLayout';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Card } from '../../components/ui/Card';
import { mockStores } from '../../utils/mockData';
import { calculateStoreStatus } from '../../utils/storeStatus';
import { useCart } from '../../hooks/useCart';
import { CheckCircle, CreditCard } from 'lucide-react';
export function Checkout() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const {
    cart,
    clearCart
  } = useCart();
  const store = mockStores.find(s => s.slug === slug);
  const [step, setStep] = useState(1);
  const [orderNumber, setOrderNumber] = useState('');
  if (!store) {
    return <div className="flex items-center justify-center min-h-screen" data-id="element-1260">
        Store not found
      </div>;
  }
  const status = calculateStoreStatus(store);
  const isOpen = status === 'open' || status === 'closing-soon';
  if (!isOpen) {
    navigate(`/store/${slug}`);
    return null;
  }
  if (cart.items.length === 0) {
    navigate(`/store/${slug}/cart`);
    return null;
  }
  const handleSubmitOrder = () => {
    const orderId = `ORD${Date.now().toString().slice(-6)}`;
    setOrderNumber(orderId);
    clearCart();
    setStep(4);
  };
  const renderStep1 = () => <Card title="Customer Information" data-id="element-1261">
      <div className="space-y-4" data-id="element-1262">
        <Input label="Email Address *" type="email" placeholder="your@email.com" data-id="element-1263" />
        <Input label="Phone Number" type="tel" placeholder="(555) 123-4567" data-id="element-1264" />
        <Input label="Student Name" placeholder="Student's full name" data-id="element-1265" />
        <div className="grid grid-cols-2 gap-4" data-id="element-1266">
          <Input label="Grade" placeholder="e.g., 9th" data-id="element-1267" />
          <Input label="Teacher" placeholder="Teacher's name" data-id="element-1268" />
        </div>
      </div>
      <div className="mt-6 flex justify-end" data-id="element-1269">
        <Button onClick={() => setStep(2)} data-id="element-1270">Continue to Shipping</Button>
      </div>
    </Card>;
  const renderStep2 = () => <Card title="Fulfillment Method" data-id="element-1271">
      <div className="space-y-4" data-id="element-1272">
        <Select label="How would you like to receive your order?" options={[{
        value: 'ship',
        label: 'Ship to My Home'
      }, {
        value: 'pickup',
        label: 'Bulk Pickup at School'
      }]} data-id="element-1273" />

        <div className="border-t border-gray-200 pt-4 mt-4" data-id="element-1274">
          <h4 className="font-medium text-gray-900 mb-3" data-id="element-1275">Shipping Address</h4>
          <div className="space-y-4" data-id="element-1276">
            <Input label="Street Address *" placeholder="123 Main St" data-id="element-1277" />
            <Input label="Apt / Suite" placeholder="Apt 4B" data-id="element-1278" />
            <div className="grid grid-cols-2 gap-4" data-id="element-1279">
              <Input label="City *" placeholder="Springfield" data-id="element-1280" />
              <Input label="State *" placeholder="IL" data-id="element-1281" />
            </div>
            <Input label="ZIP Code *" placeholder="62704" data-id="element-1282" />
          </div>
        </div>
      </div>
      <div className="mt-6 flex justify-between" data-id="element-1283">
        <Button variant="outline" onClick={() => setStep(1)} data-id="element-1284">
          Back
        </Button>
        <Button onClick={() => setStep(3)} data-id="element-1285">Continue to Payment</Button>
      </div>
    </Card>;
  const renderStep3 = () => <Card title="Payment" data-id="element-1286">
      <div className="space-y-4" data-id="element-1287">
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-1288">
          <div className="flex items-center space-x-2 mb-4" data-id="element-1289">
            <CreditCard className="h-5 w-5 text-gray-600" data-id="element-1290" />
            <span className="font-medium text-gray-900" data-id="element-1291">Credit Card</span>
          </div>
          <div className="space-y-3" data-id="element-1292">
            <Input label="Card Number" placeholder="1234 5678 9012 3456" data-id="element-1293" />
            <div className="grid grid-cols-2 gap-4" data-id="element-1294">
              <Input label="Expiry" placeholder="MM/YY" data-id="element-1295" />
              <Input label="CVV" placeholder="123" data-id="element-1296" />
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-4" data-id="element-1297">
          <h4 className="font-medium text-gray-900 mb-3" data-id="element-1298">Order Summary</h4>
          <div className="space-y-2 text-sm" data-id="element-1299">
            {cart.items.map(item => <div key={item.id} className="flex justify-between text-gray-600" data-id="element-1300">
                <span data-id="element-1301">
                  {item.productName} × {item.quantity}
                </span>
                <span data-id="element-1302">${(item.price * item.quantity).toFixed(2)}</span>
              </div>)}
            <div className="border-t border-gray-200 pt-2 mt-2" data-id="element-1303">
              <div className="flex justify-between font-bold text-gray-900" data-id="element-1304">
                <span data-id="element-1305">Total</span>
                <span data-id="element-1306">${cart.subtotal.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-6 flex justify-between" data-id="element-1307">
        <Button variant="outline" onClick={() => setStep(2)} data-id="element-1308">
          Back
        </Button>
        <Button onClick={handleSubmitOrder} data-id="element-1309">Place Order</Button>
      </div>
    </Card>;
  const renderStep4 = () => <div className="max-w-2xl mx-auto text-center py-12" data-id="element-1310">
      <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6" data-id="element-1311">
        <CheckCircle className="h-10 w-10 text-green-600" data-id="element-1312" />
      </div>
      <h1 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-1313">
        Order Confirmed!
      </h1>
      <p className="text-xl text-gray-600 mb-8" data-id="element-1314">
        Your order <span className="font-mono font-bold" data-id="element-1315">#{orderNumber}</span>{' '}
        has been received.
      </p>

      <Card className="text-left mb-8" data-id="element-1316">
        <div className="space-y-4" data-id="element-1317">
          <div data-id="element-1318">
            <h3 className="font-medium text-gray-900 mb-2" data-id="element-1319">What's Next?</h3>
            <p className="text-sm text-gray-600" data-id="element-1320">
              You'll receive a confirmation email shortly with your order
              details.
            </p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg" data-id="element-1321">
            <p className="text-sm text-blue-900" data-id="element-1322">
              <strong data-id="element-1323">Production & Delivery:</strong>{' '}
              {store.messaging.estimatedTimeline}
            </p>
          </div>
        </div>
      </Card>

      <Button onClick={() => navigate(`/store/${slug}`)} data-id="element-1324">
        Return to Store
      </Button>
    </div>;
  return <StorefrontLayout storeName={store.name} storeSlug={store.slug} organizationName={store.organizationName} supportEmail={store.supportEmail} supportPhone={store.supportPhone} data-id="element-1325">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-1326">
        {step < 4 && <>
            <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-1327">Checkout</h1>
            <div className="flex items-center space-x-2 mb-8" data-id="element-1328">
              {[1, 2, 3].map(s => <div key={s} className="flex items-center" data-id="element-1329">
                  <div className={`
                    w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                    ${s === step ? 'bg-red-600 text-white' : s < step ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600'}
                  `} data-id="element-1330">
                    {s < step ? '✓' : s}
                  </div>
                  {s < 3 && <div className="w-12 h-0.5 bg-gray-200 mx-2" data-id="element-1331" />}
                </div>)}
            </div>
          </>}

        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
        {step === 4 && renderStep4()}
      </div>
    </StorefrontLayout>;
}